/**
 * Viewability tracker that mimics IAB / MRC standards: an impression is
 * "viewable" once ≥ 50% of the ad is visible for ≥ 1 continuous second.
 * Once fired, the observer is detached.
 */
export function observeViewability(
  el: HTMLElement,
  onImpression: () => void,
  thresholdRatio = 0.5,
  durationMs = 1000,
): () => void {
  // Bail-out: no IntersectionObserver → fire immediately so we don't lose data.
  if (typeof IntersectionObserver === 'undefined') {
    onImpression();
    return () => undefined;
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  let fired = false;

  const observer = new IntersectionObserver(
    (entries) => {
      const e = entries[0];
      if (!e || fired) {
        return;
      }
      if (e.intersectionRatio >= thresholdRatio) {
        timer = setTimeout(() => {
          fired = true;
          observer.disconnect();
          onImpression();
        }, durationMs);
      } else if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
      }
    },
    { threshold: [0, thresholdRatio, 1] },
  );
  observer.observe(el);

  return (): void => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
    observer.disconnect();
  };
}
